import { createReassignActionId } from "@coworker/app/src/hooks/TaskActions/useReassignTask";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import {
  BulkUpdateTaskV1Item,
  BulkUpdateTaskV2,
} from "@coworker/apprestructured/src/task-management/services/tasks.service";
import {
  useUpdateTasksV1,
  useUpdateTasksV2,
} from "@coworker/apprestructured/src/task-management/services/useUpdateTasks";
import { TaskListItem } from "@coworker/apprestructured/src/TasksOverview/hooks/useTasksOverview";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import { useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";

const getTasksV1Payload = (
  tasks: TaskListItem[],
  {
    currentUserUid,
    teamId,
    userId,
  }: { currentUserUid: string; teamId: string | null; userId: string | null }
) => {
  const tasksV1 = tasks.filter((task) => task.type === "v1");
  return tasksV1.map<BulkUpdateTaskV1Item>((task) => ({
    task_id: task.task_data.id,
    patch: {
      action_id: createReassignActionId(),
      activity_type: userId ? "REASSIGN" : "REASSIGN_TEAM",
      assigned_team_id: teamId,
      assigned_user_id: userId,
      last_editor_id: currentUserUid,
      state: "ASSIGNED",
      undo_action_id: "",
    },
  }));
};

const getTasksV2Payload = (
  tasks: TaskListItem[],
  { teamId, userId }: { teamId: string | null; userId: string | null }
) => {
  const tasksV2 = tasks.filter((task) => task.type === "v2");
  return tasksV2.map<BulkUpdateTaskV2>((task) => ({
    id: task.task_data.id,
    patch: {
      assigned_team_id: teamId ?? undefined,
      assigned_user_id: userId ?? undefined,
    },
  }));
};

export type ResignTasksPayload = {
  teamId: string | null;
  userId: string | null;
  tasks: TaskListItem[];
  currentUserUid: string;
};

export const useReassignTasks = () => {
  const { t } = useTranslation();
  const { showToast } = useToastNotification();

  const { mutateAsync: updateTasksV1 } = useUpdateTasksV1();
  const { mutateAsync: updateTasksV2 } = useUpdateTasksV2();
  const queryClient = useQueryClient();

  const resignTasks = async ({
    teamId,
    userId,
    tasks,
    currentUserUid,
  }: ResignTasksPayload) => {
    try {
      const tasksV1Payload = getTasksV1Payload(tasks, {
        currentUserUid,
        teamId,
        userId,
      });
      const updateTaskV1Promise = tasksV1Payload.length
        ? updateTasksV1(tasksV1Payload)
        : Promise.resolve();

      const tasksV2Payload = getTasksV2Payload(tasks, {
        teamId,
        userId,
      });
      const updateTasksV2Promise = tasksV2Payload.length
        ? updateTasksV2({ tasks: tasksV2Payload })
        : Promise.resolve();

      await Promise.all([updateTaskV1Promise, updateTasksV2Promise]);
      await invalidateTaskQueries(queryClient);

      showToast(t("reassignedNTasksString", { count: tasks.length }));
    } catch (error) {
      showToast(t("failedToReassignTasksAndDeleteTeamString"));
      throw error;
    }
  };

  return { resignTasks };
};
